/**
 * useTheme Hook
 * 
 * Manages theme system with dark mode, high contrast, and system preference detection.
 * 
 * @module useTheme
 */

import { useState, useEffect, useCallback } from 'react';
import { storage, STORAGE_KEYS } from '../utils/storage';
import { logger } from '../utils/logger';

/**
 * Available themes
 */
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  HIGH_CONTRAST: 'high-contrast',
  AUTO: 'auto'
};

/**
 * Hook for theme management
 * 
 * @param {Object} [options] - Configuration options
 * @param {string} [options.defaultTheme='auto'] - Default theme
 * @returns {Object} Theme state and management functions
 */
export const useTheme = (options = {}) => {
  const { defaultTheme = THEMES.AUTO } = options;
  
  const [theme, setTheme] = useState(() => {
    // Lazy load storage to avoid initialization issues
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const saved = localStorage.getItem(`emmy-${STORAGE_KEYS.THEME}`);
        if (saved) {
          try {
            return JSON.parse(saved);
          } catch {
            return saved;
          }
        }
      }
    } catch (e) {
      // Silently fail
    }
    return defaultTheme;
  });

  const [systemPreference, setSystemPreference] = useState(() => {
    if (typeof window === 'undefined') return THEMES.LIGHT;
    return window.matchMedia('(prefers-color-scheme: dark)').matches 
      ? THEMES.DARK 
      : THEMES.LIGHT;
  });

  /**
   * Get effective theme (resolves 'auto' to system preference)
   * 
   * @returns {string} Effective theme
   */
  const getEffectiveTheme = useCallback(() => {
    if (theme === THEMES.AUTO) {
      return systemPreference;
    }
    return theme;
  }, [theme, systemPreference]);

  /**
   * Apply theme to document
   */
  const applyTheme = useCallback((themeToApply) => {
    const effectiveTheme = themeToApply === THEMES.AUTO ? systemPreference : themeToApply;
    
    document.documentElement.setAttribute('data-theme', effectiveTheme);
    document.documentElement.classList.remove('light', 'dark', 'high-contrast');
    document.documentElement.classList.add(effectiveTheme);
    
    logger.debug('Theme applied:', effectiveTheme);
  }, [systemPreference]);

  /**
   * Set theme
   * 
   * @param {string} newTheme - New theme
   */
  const setThemeHandler = useCallback((newTheme) => {
    if (Object.values(THEMES).includes(newTheme)) {
      setTheme(newTheme);
      storage.set(STORAGE_KEYS.THEME, newTheme);
      applyTheme(newTheme);
    }
  }, [applyTheme]);

  /**
   * Toggle between light and dark
   */
  const toggleTheme = useCallback(() => {
    const current = getEffectiveTheme();
    const newTheme = current === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK;
    setThemeHandler(newTheme);
  }, [getEffectiveTheme, setThemeHandler]);

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      const newPreference = e.matches ? THEMES.DARK : THEMES.LIGHT;
      setSystemPreference(newPreference);
      
      // Update if theme is set to auto
      if (theme === THEMES.AUTO) {
        applyTheme(THEMES.AUTO);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [theme, applyTheme]);

  // Apply theme on mount and when theme changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  return {
    theme,
    effectiveTheme: getEffectiveTheme(),
    systemPreference,
    setTheme: setThemeHandler,
    toggleTheme,
    THEMES
  };
};

